import React, { useState } from 'react';
import { OrganicGoo } from './loaders/index.js';
import { track } from '../lib/analytics.js';

const MAILTO = 'mailto:jb@jishutech.io';

const isValid = ({ name, email, message }) =>
  name.trim().length >= 2 &&
  /.+@.+\..+/.test(email) &&
  message.trim().length >= 10;

export default function ContactForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!isValid({ name, email, message })) {
      setError('Please add your name, a valid email, and a short note (10+ characters).');
      return;
    }
    setSubmitting(true);
    setError('');
    track('contact_submitted');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message, website, source: 'jishutech.io / Cta' }),
      });
      if (!res.ok) throw new Error(String(res.status));
      track('contact_succeeded');
      setSubmitted(true);
    } catch (err) {
      track('contact_failed', { reason: err.message || 'network' });
      setError("We couldn't reach the server. You can email us directly instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <ContactSuccess />;

  return (
    <form id="contact-form" onSubmit={handleSubmit} noValidate>
      <div className="mono-label" style={{ marginBottom: 16 }}>— Or send a note</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="field-row">
          <label className="mono-label" htmlFor="contact-name">Your name</label>
          <input id="contact-name" type="text" autoComplete="name" required
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe" />
        </div>

        <div className="field-row">
          <label className="mono-label" htmlFor="contact-email">Email</label>
          <input id="contact-email" type="email" autoComplete="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourcompany.com" />
        </div>

        <div className="field-row">
          <label className="mono-label" htmlFor="contact-message">What you're trying to ship</label>
          <textarea id="contact-message" rows="5" required
            value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="What outcome are you measuring, and by when. What you have in production today, and what it costs you. Who owns it on your side once we hand off." />
        </div>

        {/* Honeypot — hidden from humans, irresistible to bots */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
          <label htmlFor="contact-website">Website</label>
          <input id="contact-website" type="text" tabIndex="-1" autoComplete="off"
            value={website} onChange={(e) => setWebsite(e.target.value)} />
        </div>

        {error && (
          <div className="err" data-testid="contact-error">
            {error} <a href={MAILTO}>jb@jishutech.io</a>
          </div>
        )}
        {submitting && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
            <OrganicGoo label="Sending…" />
          </div>
        )}

        <button type="submit" className="btn btn-ghost"
          style={{ justifyContent: 'center', padding: '14px 18px', marginTop: 4, width: '100%' }}
          disabled={submitting}>
          {submitting ? 'Sending…' : 'Send'} <span style={{ fontFamily: 'var(--font-mono)' }}>→</span>
        </button>
      </div>
    </form>
  );
}

function ContactSuccess() {
  return (
    <div data-testid="contact-success">
      <div style={{
        width: 44, height: 44, borderRadius: '50%',
        background: 'var(--clay-wash)', border: '1px solid var(--clay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-mono)', color: 'var(--clay-deep)', fontSize: 18,
        marginBottom: 20,
      }}>✓</div>
      <div className="mono-label" style={{ marginBottom: 6, color: 'var(--clay)' }}>— Message sent</div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: 26, lineHeight: 1.2, letterSpacing: '-0.01em', margin: '0 0 12px' }}>
        Thanks — we'll be in touch.
      </h3>
      <p style={{ fontSize: 14, color: 'var(--ink-60)', margin: 0, lineHeight: 1.55 }}>
        Jonathan reads every note personally and will reply from <span style={{ fontFamily: 'var(--font-mono)' }}>jb@jishutech.io</span> within a couple of working days.
      </p>
    </div>
  );
}
